import { Box, Button, Flex, Grid, HStack, Icon, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FaUser } from 'react-icons/fa';
import { MdOutlineSignalWifiOff, MdOutlineWifi } from 'react-icons/md';

import meetingHeaderCaseBg from '@/assets/images/meetingHeaderCaseBg.svg?url';
import { ROUTES } from '@/routes/Routes';
import LiveMeetingTimer from '@/ui/Meeting/LiveMeetingTimer';
import Popconfirm from '@/ui/PopConfirm/PopConfirm';
import useNetworkConnection from '@/utils/helpers/hooks/useNetwork';

/* --------------------- comp ------------------------ */

type Props = {
  userEmail: string | null | undefined;
  currentCaseIndex: number;
  enableTimer: boolean;
  isAdmin: boolean;
  onRestartClick?: () => void;
  showRestartButton: boolean;
  title: string;
  subtitle: string;
  timerKey: string;
};

const LiveHeader = ({
  title,
  subtitle,
  timerKey,
  onRestartClick,
  showRestartButton,
  currentCaseIndex,
  userEmail,
  enableTimer,
  isAdmin,
}: Props) => {
  const { t } = useTranslation();
  const { online: isConnected } = useNetworkConnection();

  // --------------------- handlers ------------------------

  // --------------------- render ------------------------

  return (
    <Flex
      justifyContent="space-between"
      background="#FFF"
      borderBottom="1px solid rgb(233,233,233)"
      height="45px"
      gap={6}
      userSelect="none"
    >
      <Flex flexDirection="row" alignItems="center">
        <Flex
          alignItems="center"
          padding="0px 20px 0px 15px"
          color="#FFF"
          fontFamily="poppins-regular"
          fontWeight="bold"
          fontSize="12px"
          height="45px"
          style={{
            borderImage: `url(${meetingHeaderCaseBg})`,
            borderImageSlice: '25 fill',
            borderImageWidth: '48px 48px',
          }}
          maxW="50%"
        >
          <Text noOfLines={2}>
            {title}
            {currentCaseIndex !== -1 ? ` / CASUS ${currentCaseIndex + 1}` : ''}
          </Text>
        </Flex>
        <Box
          paddingLeft="15px"
          color="livelinx.black100"
          fontSize="15px"
          fontFamily="nunito-regular"
          fontWeight="500"
        >
          {subtitle}
        </Box>
      </Flex>

      {/* // --------------------- username + network ------------------------ */}

      <Flex
        paddingRight="10px"
        height="100%"
        flexDirection="row"
        // alignItems="center"
        gap="5px"
      >
        {enableTimer && <LiveMeetingTimer key={timerKey} showAfterSecond={60 * 5} />}
        <HStack color="primary" pr="10px">
          <Box
            border="1px solid"
            borderColor="primary"
            borderRadius="100%"
            w="25px"
            h="25px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={FaUser} fontSize="15px" />
          </Box>
          <Text
            fontSize="16px"
            paddingRight="10px"
            style={{
              fontVariant: 'small-caps',
            }}
          >
            {userEmail?.split('@')[0]}{' '}
          </Text>
          <Icon
            as={isConnected ? MdOutlineWifi : MdOutlineSignalWifiOff}
            color="primary"
            fontSize="15px"
          />
        </HStack>

        {/* // --------------------- ADMIN: Back to home button ------------------------ */}
        {isAdmin && (
          <Flex h="full" alignItems="center">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                window.location.href = ROUTES.HOME;
              }}
            >
              DASHBOARD
            </Button>
          </Flex>
        )}

        {/* // --------------------- restart button ------------------------ */}

        {isAdmin && showRestartButton && (
          <Grid>
            <Popconfirm
              title={
                <div>
                  {t('meeting.header.restartTitle')}
                  <br />
                  {t('meeting.header.confirmRestart')}
                </div>
              }
              onConfirm={onRestartClick ?? (() => {})}
              okText={t('common.yes') || 'Yes'}
              cancelText={t('common.no') || 'No'}
            >
              <Button padding="15px 23px  15px 21px" borderRadius="5px" size="md" variant="primary">
                {t('meeting.header.restartBtn')}
              </Button>
            </Popconfirm>
          </Grid>
        )}
      </Flex>
    </Flex>
  );
};

export default LiveHeader;
